import Modal from "../../../components/Modal/Modal";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { showToast } from "../../../ui/toast";
import { FiCheckCircle, FiClipboard } from "react-icons/fi";
import { createLabel } from "../../../services/LabelService";

const DEFAULT_LABEL_COLOR = "#489bf4";

const BUG_TEMPLATES = [
  {
    id: "flow",
    name: "Hata Akışı",
    label: "Kontrol",
    title: "Beklenen akış çalışmıyor",
    description:
      "<p><strong>Adımlar:</strong></p><ol><li></li><li></li><li></li></ol><p><strong>Beklenen Sonuç:</strong></p><p></p><p><strong>Gerçekleşen Sonuç:</strong></p><p></p>"
  },
  {
    id: "ui",
    name: "UI Düzenleme",
    label: "Arayüz",
    title: "Arayüzde düzenlenmesi gereken alan",
    description:
      "<p><strong>Ekran/Alan:</strong></p><p></p><p><strong>Mevcut Görünüm:</strong></p><p></p><p><strong>İstenen Düzenleme:</strong></p><p></p>"
  },
  {
    id: "mobile",
    name: "Mobil Uyumluluk",
    label: "Mobil",
    title: "Mobil görünümde sorun var",
    description:
      "<p><strong>Cihaz/Tarayıcı:</strong></p><p></p><p><strong>Sorun:</strong></p><p></p><p><strong>Beklenen Görünüm:</strong></p><p></p>"
  },
  {
    id: "performance",
    name: "Performans",
    label: "Hız",
    title: "Sayfa veya işlem yavaş çalışıyor",
    description:
      "<p><strong>Nerede:</strong></p><p></p><p><strong>Ne Zaman Oluyor:</strong></p><p></p><p><strong>Beklenen Performans:</strong></p><p></p>"
  }
];

const formatBytes = (bytes) => {
  const n = Number(bytes || 0);
  if (!n) return "";
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(1)} GB`;
};

const isImageType = (file) => {
  const t = String(file?.type || "");
  return t.startsWith("image/");
};

const isImageUrl = (u) => {
  const s = String(u || "");
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(s.split("?")[0]);
};

const BugModal = ({
  open,
  close,
  form,
  setForm,
  upload,
  add,
  isEdit = false,
  save,
  projectLabels = [],
  projectId,
  onLabelCreated,
  copyDevelopmentPrompt,
  copyTestScenarioPrompt
}) => {
  const selectedLabelIds = useMemo(
    () => new Set((Array.isArray(form?.labels) ? form.labels : []).map((id) => String(id))),
    [form?.labels]
  );

  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelColor, setNewLabelColor] = useState(DEFAULT_LABEL_COLOR);
  const [labelCreating, setLabelCreating] = useState(false);

  const selectLabel = (labelId) => {
    const id = String(labelId);
    setForm((f) => {
      const current = (Array.isArray(f?.labels) ? f.labels : []).map((x) => String(x));
      return current.includes(id) ? f : { ...f, labels: [...current, id] };
    });
  };

  const toggleLabel = (labelId) => {
    const id = String(labelId);
    setForm((f) => {
      const current = (Array.isArray(f?.labels) ? f.labels : []).map((x) => String(x));
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      return { ...f, labels: next };
    });
  };

  const handleCreateLabel = async () => {
    const name = String(newLabelName || "").trim();
    if (!name || !projectId || labelCreating) return;

    setLabelCreating(true);
    try {
      const response = await createLabel({
        projectId,
        name,
        color: newLabelColor || DEFAULT_LABEL_COLOR
      });
      const created = response?.label;
      if (created?.id != null) {
        if (typeof onLabelCreated === "function") {
          onLabelCreated(created);
        }
        selectLabel(created.id);
      }
      setNewLabelName("");
      setNewLabelColor(DEFAULT_LABEL_COLOR);
    } catch (error) {
      showToast({
        title: "Etiket eklenemedi",
        description: error?.message || "Bir sorun oluştu.",
        variant: "error",
        position: "bottom-right"
      });
    } finally {
      setLabelCreating(false);
    }
  };
  const hasDesc = useMemo(() => {
    const plain = String(form?.description || "").replace(/<[^>]+>/g, "").trim();
    return plain.length > 0;
  }, [form?.description]);
  const plainTitle = useMemo(() => String(form?.title || "").trim(), [form?.title]);
  const plainDescription = useMemo(
    () => String(form?.description || "").replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim(),
    [form?.description]
  );

  const [showDetails, setShowDetails] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const fileInputRef = useRef(null);
  const formRef = useRef(form);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    if (!open) return;
    setShowDetails(hasDesc);
  }, [open, hasDesc]);

  useEffect(() => {
    if (!open) return;
    setSelectedTemplateId("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!form?.file) {
      setLocalPreviewUrl("");
      return;
    }
    if (!isImageType(form.file)) {
      setLocalPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(form.file);
    setLocalPreviewUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch {
      }
    };
  }, [open, form?.file]);

  const pickFile = (file) => {
    if (!file) return;
    setForm((f) => ({
      ...f,
      file,
      fileUrl: "",
      uploadStatus: ""
    }));
  };

  const clearFile = () => {
    setForm((f) => ({
      ...f,
      file: null,
      fileUrl: "",
      uploadStatus: ""
    }));
    setLocalPreviewUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer?.files?.[0];
    if (file) pickFile(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const current = e.currentTarget;
    const related = e.relatedTarget;
    if (current && related && current.contains(related)) return;
    setIsDragging(false);
  };

  const waitForFileUrl = () => {
    return new Promise((resolve) => {
      let tries = 0;

      const tick = () => {
        const currentUrl = String(formRef.current?.fileUrl || "");
        if (currentUrl) {
          resolve(currentUrl);
          return;
        }
        tries += 1;
        if (tries > 80) {
          resolve("");
          return;
        }
        setTimeout(tick, 100);
      };

      tick();
    });
  };

  const ensureUploadedIfNeeded = async () => {
    const hasFile = !!formRef.current?.file;
    const hasUrl = !!String(formRef.current?.fileUrl || "");
    if (!hasFile) return String(formRef.current?.fileUrl || "");
    if (hasUrl) return String(formRef.current?.fileUrl || "");

    try {
      const maybeUrl = await upload();
      if (typeof maybeUrl === "string" && maybeUrl) return maybeUrl;
    } catch {
    }

    const waited = await waitForFileUrl();
    return waited;
  };

  const applyTemplate = (template) => {
    if (!template || isEdit) return;
    setSelectedTemplateId(template.id);
    setShowDetails(true);
    setForm((f) => ({
      ...f,
      title: template.title,
      description: template.description
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formRef.current?.uploadStatus === "Yükleniyor..." || submitting) return;

    setSubmitting(true);
    try {
      const url = await ensureUploadedIfNeeded();

      if (formRef.current?.file && !url) {
        showToast({
          title: "Hata",
          description: "Dosya yüklenemedi. Tekrar deneyin.",
          variant: "error",
          position: "bottom-right"
        });
        setSubmitting(false);
        return;
      }

      if (isEdit && typeof save === "function") {
        save({
          id: formRef.current?.id,
          title: formRef.current?.title,
          description: formRef.current?.description,
          file_url: url || formRef.current?.fileUrl || undefined,
          version_name: formRef.current?.version || undefined
        });
      } else {
        add();
      }

      close();
    } finally {
      setSubmitting(false);
    }
  };

  const selectedName = String(form?.file?.name || "");
  const selectedSize = form?.file?.size ? formatBytes(form.file.size) : "";
  const selectedType = String(form?.file?.type || "");
  const titleCount = plainTitle.length;
  const detailCount = plainDescription.length;
  const canBuildPrompt = !!(
    plainTitle ||
    plainDescription ||
    form?.fileUrl ||
    form?.file ||
    form?.externalUrl
  );

  return (
    <Modal open={open} close={close} cardStyle={{ padding: "0px" }}>
      <div className="bugmodal-wrapper">
        <div className="bugmodal-header">
          <h2>{isEdit ? "İşi Düzenle" : "Yeni İş Ekle"}</h2>
        </div>

        <form onSubmit={handleSubmit} className="bugmodal-body">
          {!isEdit && (
            <div className="bugmodal-section bugmodal-template-section">
              <div className="bugmodal-section-head bugmodal-section-head-split">
                <div className="bugmodal-section-copy">
                  <span className="bugmodal-section-kicker">Şablon</span>
                  <div className="bugmodal-section-label">Bug Şablonu</div>
                </div>

                <span className="bugmodal-meta-pill subtle">
                  <FiClipboard size={14} />
                  Hazır içerik
                </span>
              </div>

              <div className="bugmodal-template-grid">
                {BUG_TEMPLATES.map((template) => {
                  const active = selectedTemplateId === template.id;
                  return (
                    <button
                      key={template.id}
                      type="button"
                      className={`bugmodal-template-card${active ? " active" : ""}`}
                      onClick={() => applyTemplate(template)}
                    >
                      <span className="bugmodal-template-icon">
                        {active ? <FiCheckCircle size={18} /> : <FiClipboard size={18} />}
                      </span>
                      <span className="bugmodal-template-copy">
                        <strong>{template.name}</strong>
                        <small>{template.label}</small>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bugmodal-section bugmodal-section-title">
            <div className="bugmodal-section-head bugmodal-section-head-split">
              <div className="bugmodal-section-copy">
                <span className="bugmodal-section-kicker">Yeni İş</span>
                <div className="bugmodal-section-label">İş Başlığı</div>
                <p className="bugmodal-section-note">
                  Ekibin hızlı anlayacağı, net ve kısa bir başlık yaz.
                </p>
              </div>

              <div className="bugmodal-meta-row">
                <span className={`bugmodal-meta-pill${titleCount ? " filled" : ""}`}>
                  {titleCount ? "Hazır" : "Boş"}
                </span>
                <span className="bugmodal-meta-pill subtle">{titleCount} karakter</span>
              </div>
            </div>

            <div className="bugmodal-title-shell">
              <input
                className="bugmodal-title"
                placeholder="Örnek: Login ekranında hata mesajı görünmüyor"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                autoFocus
              />
              <div className="bugmodal-title-footer">
                <span>Kısa ve çözüme dönük yaz</span>
                <span>Takım görünürlüğü yüksek başlıklar daha hızlı ilerler</span>
              </div>
            </div>

            <div className="bugmodal-labels">
              <div className="bugmodal-labels-head">
                <span className="bugmodal-section-kicker">Etiketler</span>
                <span className="bugmodal-meta-pill subtle">{selectedLabelIds.size} seçili</span>
              </div>

              <div className="bugmodal-label-create">
                <input
                  type="color"
                  className="bugmodal-label-color"
                  value={newLabelColor}
                  onChange={(e) => setNewLabelColor(e.target.value)}
                  title="Etiket rengi"
                  disabled={labelCreating}
                />
                <input
                  type="text"
                  className="bugmodal-label-name"
                  placeholder="Yeni etiket adı"
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCreateLabel();
                    }
                  }}
                  disabled={labelCreating}
                  maxLength={50}
                />
                <button
                  type="button"
                  className="bugmodal-label-create-btn"
                  onClick={handleCreateLabel}
                  disabled={labelCreating || !String(newLabelName || "").trim()}
                >
                  {labelCreating ? "Ekleniyor..." : "Etiket Oluştur"}
                </button>
              </div>

              {projectLabels.length > 0 ? (
                <div className="bugmodal-labels-grid">
                  {projectLabels.map((label) => {
                    const labelId = String(label.id);
                    const color = label.color || "#489bf4";
                    const active = selectedLabelIds.has(labelId);
                    return (
                      <button
                        key={labelId}
                        type="button"
                        className={`bugmodal-label-chip${active ? " active" : ""}`}
                        onClick={() => toggleLabel(labelId)}
                        style={
                          active
                            ? {
                                backgroundColor: `${color}1a`,
                                color,
                                borderColor: `${color}80`
                              }
                            : undefined
                        }
                      >
                        <span
                          className="bugmodal-label-dot"
                          style={{ backgroundColor: color }}
                        />
                        {label.name}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="bugmodal-labels-empty">
                  Henüz etiket yok. Yukarıdan yeni bir etiket oluşturup bu işe ekleyebilirsin.
                </p>
              )}
            </div>

            <div className="bugmodal-meta-fields">
              <label className="bugmodal-meta-field">
                <span className="bugmodal-section-kicker">Öncelik</span>
                <select
                  className="bugmodal-priority-select"
                  value={form.priority || "medium"}
                  onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                >
                  <option value="low">Düşük</option>
                  <option value="medium">Orta</option>
                  <option value="high">Yüksek</option>
                </select>
              </label>

              <label className="bugmodal-meta-field">
                <span className="bugmodal-section-kicker">Teslim Tarihi</span>
                <input
                  type="date"
                  className="bugmodal-due-input"
                  value={form.dueDate || ""}
                  onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
                />
              </label>
            </div>
          </div>

          <div className={`bugmodal-section bugmodal-section-details${showDetails ? " open" : ""}`}>
            <div className="bugmodal-section-head bugmodal-section-head-inline">
              <div className="bugmodal-section-copy">
                <span className="bugmodal-section-kicker">Detay</span>
                <div className="bugmodal-section-label">Daha Fazla Detay</div>
                <p className="bugmodal-section-note">
                  Açıklama, ekler ve bağlantılarla işi netleştir.
                </p>
              </div>

              <div className="bugmodal-meta-row">
                <span className={`bugmodal-meta-pill${hasDesc ? " filled" : ""}`}>
                  {hasDesc ? "Dolu" : "Boş"}
                </span>
                <span className="bugmodal-meta-pill subtle">{detailCount} karakter</span>
              </div>

              <button
                type="button"
                className="bugmodal-details-toggle"
                onClick={() => setShowDetails((s) => !s)}
              >
                <span className="bugmodal-details-toggle-copy">
                  {showDetails ? "Detayı Gizle" : "Detay Alanını Aç"}
                </span>
                <span className="bugmodal-details-toggle-indicator">
                  {showDetails ? "−" : "+"}
                </span>
              </button>
            </div>

            {showDetails && (
              <div className="bugmodal-details-panel">
                <div className="bugmodal-editor-shell">
                  <div className="bugmodal-editor-head">
                    <span>Açıklama</span>
                    <span>Zengin metin</span>
                  </div>

                  <ReactQuill
                    value={form.description}
                    onChange={(v) => setForm((f) => ({ ...f, description: v }))}
                    className="bugmodal-editor"
                  />
                </div>

                <div className="bugmodal-detail-hint">
                  <span className="bugmodal-detail-hint-dot" />
                  <span>Beklenen davranış, hata adımları ve varsa ekran görüntüsünü ekle.</span>
                </div>

                <div className="bugmodal-upload">
                <input
                  ref={fileInputRef}
                  type="file"
                  id="filepick"
                  onChange={(e) => pickFile(e.target.files?.[0])}
                />

                <label
                  htmlFor="filepick"
                  className={isDragging ? "dragging" : ""}
                  onDragEnter={onDragEnter}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                >
                  {isDragging ? "Bırak ve ekle" : "Dosya Seç veya sürükleyip bırak"}
                </label>

                <button
                  type="button"
                  onClick={upload}
                  disabled={!form.file || form.uploadStatus === "Yükleniyor..." || submitting}
                >
                  {form.uploadStatus || "Yükle"}
                </button>
                </div>

                {(selectedName || form.fileUrl) && (
                  <div className="bugmodal-filemeta">
                  {selectedName && (
                      <div className="bugmodal-filemeta-row">
                      <div className="bugmodal-filemeta-left">
                        <div className="bugmodal-filemeta-name">{selectedName}</div>
                        <div className="bugmodal-filemeta-sub">
                          {selectedSize ? `${selectedSize}${selectedType ? ` · ${selectedType}` : ""}` : selectedType}
                        </div>
                      </div>

                        <button
                        type="button"
                        className="bugmodal-filemeta-remove"
                        onClick={clearFile}
                        disabled={form.uploadStatus === "Yükleniyor..." || submitting}
                      >
                        Kaldır
                        </button>
                      </div>
                  )}

                  {localPreviewUrl && (
                      <div className="bugmodal-preview">
                      <img src={localPreviewUrl} alt="Önizleme" />
                      </div>
                  )}

                  {form.fileUrl && (
                      <>
                      {isImageUrl(form.fileUrl) && (
                        <div className="bugmodal-preview">
                          <img src={form.fileUrl} alt="Yüklenen dosya" />
                        </div>
                      )}
                      <a
                        href={form.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bugmodal-link"
                      >
                        Dosyayı Görüntüle
                      </a>
                      </>
                  )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bugmodal-actions">
            <button
              type="submit"
              disabled={form.uploadStatus === "Yükleniyor..." || submitting}
            >
              {isEdit ? "Güncelle" : "Ekle"}
            </button>
            {typeof copyDevelopmentPrompt === "function" && (
              <button
                type="button"
                className="bugmodal-prompt-button"
                onClick={copyDevelopmentPrompt}
                disabled={!canBuildPrompt || submitting}
                title={isEdit ? "Geliştirme promptu kopyala" : "Taslak geliştirme promptu kopyala"}
              >
                Geliştir
              </button>
            )}
            {typeof copyTestScenarioPrompt === "function" && (
              <button
                type="button"
                className="bugmodal-prompt-button"
                onClick={copyTestScenarioPrompt}
                disabled={!canBuildPrompt || submitting}
                title={isEdit ? "Test senaryosu promptu kopyala" : "Taslak test senaryosu promptu kopyala"}
              >
                Test Senaryosu
              </button>
            )}
            <button type="button" onClick={close} disabled={submitting}>
              Kapat
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default BugModal;
